import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FEEDBACK_MEDIA_MAX_FILES,
  submitFeedback,
  uploadFeedbackMediaFile,
  validateFeedbackMediaFile,
} from '../../../services/feedbackService';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { colors, typography } from '../../../styles/theme';
import { toast } from '../../../Components/Toast';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../../hooks/useScrollEdgeFade';

export const LOT_ERROR_CATEGORY = 'lote';

const CATEGORIES = [
  {
    key: 'bug',
    label: 'Reportar error',
    description: 'Comportamiento incorrecto o inesperado en la app',
    descriptionPlaceholder: 'Describí qué ocurrió y, si podés, cómo reproducir el problema.',
  },
  {
    key: LOT_ERROR_CATEGORY,
    label: 'Error de lote',
    description: 'Datos incorrectos o inconsistentes en un lote específico',
    descriptionPlaceholder: 'Describí qué información es incorrecta, incompleta o falta.',
  },
  {
    key: 'mejora',
    label: 'Sugerir mejora',
    description: 'Idea para mejorar o agregar funcionalidad',
    descriptionPlaceholder: '¿Qué cambiarías o agregarías?',
  },
  {
    key: 'comentario',
    label: 'Comentario general',
    description: 'Mensaje, opinión o consulta general',
    descriptionPlaceholder: 'Escribí tu mensaje...',
  },
];

const isMobile = window.innerWidth < 600;
const FEEDBACK_MODAL_TEXT_COLOR = '#FBFBFB';
const FEEDBACK_MODAL_BORDER_COLOR = '#888888';

const initialForm = {
  category: 'bug',
  title: '',
  description: '',
  email: '',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FEEDBACK_MEDIA_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
].join(',');

const formatMediaSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 1) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
};

const validateField = (name, value, category) => {
  if (name === 'category') {
    if (!category) return 'Seleccioná una categoría.';
    return '';
  }

  if (name === 'title') {
    const trimmed = value.trim();
    if (!trimmed) return 'El título es obligatorio.';
    if (trimmed.length < 3) return 'El título debe tener al menos 3 caracteres.';
    if (trimmed.length > 120) return 'El título no puede superar 120 caracteres.';
    return '';
  }

  if (name === 'description') {
    const trimmed = value.trim();
    if (!trimmed) return 'La descripción es obligatoria.';
    if (trimmed.length < 10) return 'La descripción debe tener al menos 10 caracteres.';
    if (trimmed.length > 3000) return 'La descripción no puede superar 3000 caracteres.';
    return '';
  }

  if (name === 'email') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!emailRegex.test(trimmed)) return 'Ingresá un email válido.';
    return '';
  }

  return '';
};

const buildErrors = (form) => ({
  category: validateField('category', '', form.category),
  title: validateField('title', form.title, form.category),
  description: validateField('description', form.description, form.category),
  email: validateField('email', form.email, form.category),
});

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.66)',
  zIndex: 2200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  animation: 'feedbackOverlayFadeIn 180ms ease-out',
};


const MODAL_PADDING_X = 20;
const MODAL_PADDING_TOP = 20;
const MODAL_PADDING_BOTTOM = 16;

const modalStyle = {
  width: 'min(628px, 95vw)',
  height: isMobile ? 'min(680px, 70vh)' : 'min(700px, 90vh)',
  overflow: 'hidden',
  background: colors.backgroundSurface,
  color: FEEDBACK_MODAL_TEXT_COLOR,
  borderRadius: '12px',
  boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
  padding: 0,
  animation: 'feedbackModalSlideUp 220ms ease-out',
  fontFamily: typography.fontFamily.primary,
};

const inputStyle = {
  width: '100%',
  borderRadius: '8px',
  border: `1px solid ${FEEDBACK_MODAL_BORDER_COLOR}`,
  backgroundColor: colors.backgroundSurface,
  color: FEEDBACK_MODAL_TEXT_COLOR,
  padding: '10px 12px',
  fontSize: '14px',
  boxSizing: 'border-box',
  outline: 'none',
};

const disabledInputStyle = {
  backgroundColor: colors.backgroundDark,
};

const inputInnerStyle = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  color: FEEDBACK_MODAL_TEXT_COLOR,
  fontSize: '14px',
  outline: 'none',
  padding: 0,
  fontFamily: 'inherit',
};

const inputWithAddonStyle = {
  ...inputStyle,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const errorStyle = {
  marginTop: '6px',
  color: '#f2b8b5',
  fontSize: '12px',
};

const buildParcelForm = (lotId) => ({
  ...initialForm,
  category: LOT_ERROR_CATEGORY,
  title: lotId,
});

const categoryTriggerStyle = ({ isOpen = false, disabled = false } = {}) => ({
  ...inputStyle,
  ...(disabled ? disabledInputStyle : null),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.85 : 1,
  textAlign: 'left',
});

const categoryMenuStyle = {
  backgroundColor: colors.backgroundSurface,
  borderRadius: '8px',
  border: `1px solid ${FEEDBACK_MODAL_BORDER_COLOR}`,
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
  overflow: 'hidden',
  padding: 0,
};

const categoryMenuItemStyle = ({ isHovered = false } = {}) => ({
  width: '100%',
  display: 'block',
  padding: '10px 12px',
  border: 'none',
  backgroundColor: isHovered ? colors.primary : colors.backgroundSurface,
  color: FEEDBACK_MODAL_TEXT_COLOR,
  fontSize: '14px',
  fontFamily: typography.fontFamily.primary,
  cursor: 'pointer',
  textAlign: 'left',
});

const categoryMenuDividerStyle = {
  height: '1px',
  backgroundColor: FEEDBACK_MODAL_BORDER_COLOR,
};

function FeedbackCategorySelect({ id, value, options, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);

  const selectedOption = options.find((option) => option.key === value);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 2300,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (event.target.closest('[data-feedback-category-menu]')) return;
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (optionKey) => {
    onChange(optionKey);
    setIsOpen(false);
    setHoveredKey(null);
  };

  if (disabled) {
    return (
      <div id={id} style={categoryTriggerStyle({ disabled: true })}>
        <span>{selectedOption?.label || 'Seleccionar categoría'}</span>
      </div>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        style={categoryTriggerStyle({ isOpen })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{selectedOption?.label || 'Seleccionar categoría'}</span>
        {isOpen
          ? <KeyboardArrowUpIcon sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />}
      </button>

      {isOpen && menuPosition && createPortal(
        <div
          data-feedback-category-menu
          role="listbox"
          aria-labelledby={id}
          style={{ ...categoryMenuStyle, ...menuPosition }}
        >
          {options.map((option, index) => (
            <React.Fragment key={option.key}>
              {index > 0 && <div style={categoryMenuDividerStyle} aria-hidden="true" />}
              <button
                type="button"
                role="option"
                aria-selected={option.key === value}
                style={categoryMenuItemStyle({ isHovered: hoveredKey === option.key })}
                onClick={() => handleSelect(option.key)}
                onMouseEnter={() => setHoveredKey(option.key)}
                onMouseLeave={() => setHoveredKey(null)}
              >
                {option.label}
              </button>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

const FeedbackModal = ({ isOpen, onClose, lotId = null }) => {
  const isParcelReport = Boolean(lotId);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [mediaAttachments, setMediaAttachments] = useState([]);
  const [mediaError, setMediaError] = useState('');
  const mediaInputRef = useRef(null);
  const { scrollRef: formScrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setForm(initialForm);
      setErrors({});
      setHasAttemptedSubmit(false);
      setIsSubmitting(false);
      setSubmitError('');
      setMediaAttachments((prev) => {
        prev.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      setMediaError('');
      return;
    }

    if (lotId) {
      setForm(buildParcelForm(lotId));
      setErrors({});
      setHasAttemptedSubmit(false);
      setIsSubmitting(false);
      setSubmitError('');
      setMediaAttachments((prev) => {
        prev.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      setMediaError('');
    }
  }, [isOpen, lotId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = window.requestAnimationFrame(() => refreshScrollEdges());
    return () => window.cancelAnimationFrame(id);
  }, [
    isOpen,
    refreshScrollEdges,
    mediaAttachments.length,
    mediaError,
    submitError,
    hasAttemptedSubmit,
    errors,
  ]);

  const selectedCategory = useMemo(
    () => CATEGORIES.find((category) => category.key === form.category),
    [form.category]
  );

  const visibleCategories = useMemo(
    () => (isParcelReport
      ? CATEGORIES.filter((category) => category.key === LOT_ERROR_CATEGORY)
      : CATEGORIES.filter((category) => category.key !== LOT_ERROR_CATEGORY)),
    [isParcelReport]
  );

  if (!isOpen) return null;

  const onFieldChange = (field, value) => {
    const nextForm = { ...form, [field]: value };
    setForm(nextForm);
    setSubmitError('');

    if (hasAttemptedSubmit) {
      setErrors(buildErrors(nextForm));
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  const handleAddMediaFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    setMediaError('');
    setSubmitError('');

    const available = FEEDBACK_MEDIA_MAX_FILES - mediaAttachments.length;
    if (available <= 0) {
      setMediaError(`Podés adjuntar hasta ${FEEDBACK_MEDIA_MAX_FILES} archivos.`);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }

    const accepted = [];
    let lastError = '';
    for (const file of incoming) {
      if (accepted.length >= available) {
        lastError = `Podés adjuntar hasta ${FEEDBACK_MEDIA_MAX_FILES} archivos.`;
        break;
      }
      const validation = validateFeedbackMediaFile(file);
      if (!validation.ok) {
        lastError = validation.message;
        continue;
      }
      accepted.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        kind: validation.kind,
        contentType: validation.contentType,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (accepted.length) {
      setMediaAttachments((prev) => [...prev, ...accepted]);
    }
    if (lastError) setMediaError(lastError);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const handleRemoveMedia = (id) => {
    setMediaAttachments((prev) => {
      const next = [];
      prev.forEach((item) => {
        if (item.id === id) {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        } else {
          next.push(item);
        }
      });
      return next;
    });
    setMediaError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setHasAttemptedSubmit(true);
    const nextErrors = buildErrors(form);
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const media = [];
      for (const attachment of mediaAttachments) {
        const uploaded = await uploadFeedbackMediaFile(attachment.file);
        media.push(uploaded);
      }

      const payload = {
        categoria: form.category,
        titulo: form.title.trim(),
        descripcion: form.description.trim(),
        email: form.email.trim() || null,
      };
      if (media.length) {
        payload.media = media;
      }

      await submitFeedback(payload);
      handleClose();
      toast.success({
        title: '¡Gracias por tu feedback!',
        message: 'Lo tendremos en cuenta para seguir mejorando.',
      });
    } catch (error) {
      setSubmitError(error.message || 'Error de red. Reintentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div style={overlayStyle} onMouseDown={handleOverlayClick} role="presentation">
      <style>
        {`
          @keyframes feedbackOverlayFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes feedbackModalSlideUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes feedbackSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .feedback-email-input::placeholder {
            color: ${colors.textSecondary};
            opacity: 1;
          }
        `}
      </style>

      <form
        style={{
          ...modalStyle,
          display: 'flex',
          flexDirection: 'column',
        }}
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-label="Formulario de feedback"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
            padding: `${MODAL_PADDING_TOP}px ${MODAL_PADDING_X}px 0`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '22px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            {isParcelReport ? 'REPORTAR ERROR' : 'ENVIAR FEEDBACK'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar modal de feedback"
            style={{
              width: '32px',
              height: '32px',
              border: 'none',
              borderRadius: '50%',
              background: colors.backgroundSurface,
              color: FEEDBACK_MODAL_TEXT_COLOR,
              cursor: isSubmitting ? 'default' : 'pointer',
              fontSize: '18px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            disabled={isSubmitting}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div
          role="separator"
          aria-hidden="true"
          style={{
            marginTop: '16px',
            borderTop: `1px solid ${FEEDBACK_MODAL_BORDER_COLOR}`,
          }}
        />

        <p
          style={{
            margin: '16px 0 0',
            padding: `0 ${MODAL_PADDING_X}px`,
            color: FEEDBACK_MODAL_TEXT_COLOR,
            fontSize: '13px',
          }}
        >
          {isParcelReport
            ? 'Reportá información incorrecta, incompleta o inconsistente de este lote.'
            : 'Compartí sugerencias, comentarios o problemas que encontraste mientras usabas Terrio.'}
        </p>

        <div
          ref={formScrollRef}
          style={{
            marginTop: '16px',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: `0 ${MODAL_PADDING_X}px`,
            paddingRight: `${MODAL_PADDING_X + 4}px`,
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
        >
          <div>
          <label htmlFor="feedback-category" style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            Categoría
          </label>
          <FeedbackCategorySelect
            id="feedback-category"
            value={form.category}
            options={visibleCategories}
            onChange={(categoryKey) => onFieldChange('category', categoryKey)}
            disabled={isParcelReport}
          />
          <div style={{ marginTop: '6px', color: FEEDBACK_MODAL_TEXT_COLOR, fontSize: '12px' }}>
            {selectedCategory?.description}
          </div>
          {hasAttemptedSubmit && errors.category && <div style={errorStyle}>{errors.category}</div>}
          </div>

        <div style={{ marginTop: '14px' }}>
          <label htmlFor="feedback-title" style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            {isParcelReport ? 'ID de lote (Terrio)' : 'Título'}
          </label>
          <input
            id="feedback-title"
            type="text"
            maxLength={120}
            value={form.title}
            onChange={(e) => onFieldChange('title', e.target.value)}
            readOnly={isParcelReport}
            placeholder={isParcelReport ? '' : 'Escribí un título para tu feedback'}
            style={{
              ...inputStyle,
              ...(isParcelReport ? disabledInputStyle : null),
              padding: '10px 12px',
              fontSize: '14px',
              boxSizing: 'border-box',
              outline: 'none',
              opacity: isParcelReport ? 0.85 : 1,
              cursor: isParcelReport ? 'default' : 'text',
            }}
          />
          {!isParcelReport && (
            <div style={{ marginTop: '4px', textAlign: 'right', fontSize: '11px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
              {form.title.length}/120
            </div>
          )}
          {hasAttemptedSubmit && errors.title && <div style={errorStyle}>{errors.title}</div>}
        </div>

        <div style={{ marginTop: '14px' }}>
          <label htmlFor="feedback-description" style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            Descripción del problema
          </label>
          <textarea
            id="feedback-description"
            maxLength={3000}
            value={form.description}
            onChange={(e) => onFieldChange('description', e.target.value)}
            placeholder={selectedCategory?.descriptionPlaceholder || 'Seleccioná una categoría para continuar...'}
            style={{
              ...inputStyle,
              height: '160px',
              resize: 'none',
              overflowY: 'auto',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ marginTop: '4px', textAlign: 'right', fontSize: '11px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            {form.description.length}/3000
          </div>
          {hasAttemptedSubmit && errors.description && <div style={errorStyle}>{errors.description}</div>}
        </div>

        <div style={{ marginTop: '0px' }}>
          <label
            htmlFor="feedback-media"
            style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: FEEDBACK_MODAL_TEXT_COLOR }}
          >
            Adjuntos (opcional)
          </label>
          <p
            id="feedback-media-help"
            style={{ margin: '0 0 8px', fontSize: '12px', color: FEEDBACK_MODAL_TEXT_COLOR, opacity: 0.85 }}
          >
            Hasta {FEEDBACK_MEDIA_MAX_FILES} · Podés sumar fotos (hasta 8 MB) o videos (hasta 40 MB)
          </p>

          <input
            id="feedback-media"
            ref={mediaInputRef}
            type="file"
            accept={FEEDBACK_MEDIA_ACCEPT}
            multiple
            disabled={isSubmitting || mediaAttachments.length >= FEEDBACK_MEDIA_MAX_FILES}
            onChange={(event) => handleAddMediaFiles(event.target.files)}
            aria-describedby="feedback-media-help"
            style={{ display: 'none' }}
          />

          <button
            type="button"
            disabled={isSubmitting || mediaAttachments.length >= FEEDBACK_MEDIA_MAX_FILES}
            onClick={() => mediaInputRef.current?.click()}
            aria-describedby="feedback-media-help"
            style={{
              ...inputStyle,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              minHeight: '44px',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              color: FEEDBACK_MODAL_TEXT_COLOR,
              cursor: (isSubmitting || mediaAttachments.length >= FEEDBACK_MEDIA_MAX_FILES) ? 'not-allowed' : 'pointer',
              opacity: (isSubmitting || mediaAttachments.length >= FEEDBACK_MEDIA_MAX_FILES) ? 0.55 : 1,
              backgroundColor: 'transparent',
            }}
          >
            <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
            <span style={{ fontSize: '13px' }}>
              {mediaAttachments.length >= FEEDBACK_MEDIA_MAX_FILES
                ? 'Límite de adjuntos alcanzado'
                : 'Adjuntar foto o video'}
            </span>
          </button>

          {mediaAttachments.length > 0 && (
            <div
              style={{
                marginTop: '10px',
                padding: '8px',
                border: `1px solid ${FEEDBACK_MODAL_BORDER_COLOR}`,
                borderRadius: '8px',
                backgroundColor: colors.backgroundDark,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: '8px',
                boxSizing: 'border-box',
              }}
            >
              {mediaAttachments.map((item) => (
                <div
                  key={item.id}
                  style={{
                    position: 'relative',
                    borderRadius: '8px',
                    border: `1px solid ${FEEDBACK_MODAL_BORDER_COLOR}`,
                    overflow: 'hidden',
                    backgroundColor: colors.backgroundSurface,
                    aspectRatio: '1 / 1',
                  }}
                >
                  {item.kind === 'image' ? (
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        boxSizing: 'border-box',
                        color: FEEDBACK_MODAL_TEXT_COLOR,
                      }}
                    >
                      <VideocamOutlinedIcon sx={{ fontSize: 28, color: colors.textSecondary }} />
                      <span
                        style={{
                          fontSize: '11px',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                        title={item.file.name}
                      >
                        {item.file.name}
                      </span>
                      <span style={{ fontSize: '10px', opacity: 0.75 }}>
                        {formatMediaSize(item.file.size)}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Quitar ${item.file.name}`}
                    disabled={isSubmitting}
                    onClick={() => handleRemoveMedia(item.id)}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '24px',
                      height: '24px',
                      border: 'none',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.65)',
                      color: '#fff',
                      cursor: isSubmitting ? 'default' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {mediaError && <div style={errorStyle}>{mediaError}</div>}
        </div>

        <div style={{ marginTop: '14px' }}>
          <label htmlFor="feedback-email" style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: FEEDBACK_MODAL_TEXT_COLOR }}>
            Email o usuario
          </label>
          <div style={inputWithAddonStyle}>
            <EmailOutlinedIcon
              sx={{ fontSize: 18, color: colors.textSecondary, flexShrink: 0 }}
              aria-hidden="true"
            />
            <input
              id="feedback-email"
              type="email"
              className="feedback-email-input"
              value={form.email}
              onChange={(e) => onFieldChange('email', e.target.value)}
              placeholder="usuario@email.com"
              style={inputInnerStyle}
            />
          </div>
          {hasAttemptedSubmit && errors.email && <div style={errorStyle}>{errors.email}</div>}
        </div>

        {submitError && (
          <div
            style={{
              marginTop: '14px',
              borderRadius: '8px',
              border: '1px solid rgba(242,184,181,0.42)',
              background: 'rgba(242,184,181,0.08)',
              color: '#f2b8b5',
              fontSize: '13px',
              padding: '10px 12px',
            }}
          >
            {submitError}
          </div>
        )}
        </div>

        <div
          style={{
            marginTop: '14px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: `14px ${MODAL_PADDING_X}px ${MODAL_PADDING_BOTTOM}px`,
          }}
        >
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              borderRadius: '8px',
              border: 'none',
              background: isSubmitting ? 'rgba(72,146,145,0.45)' : colors.primary,
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '14px',
              padding: '10px 16px',
              height: '40px',
              minWidth: '156px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {isSubmitting && (
              <span
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: '#ffffff',
                  animation: 'feedbackSpin 0.7s linear infinite',
                }}
              />
            )}
            {isSubmitting ? 'Enviando...' : (isParcelReport ? 'Enviar reporte' : 'Enviar')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default FeedbackModal;
